<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->
<div class="container">
  @if (msel) {
  <!-- search field bar; sits outside the scrolling table so it stays visible -->
  @if (showSearch) {
  <div class="search-bar">
    <span class="center-self">Search</span>
    <mat-form-field class="center-self shift-left">
      <input matInput [(ngModel)]="filterString" (ngModelChange)="filterChange.next($event)" />
      @if (filterString) {
      <button mat-icon-button matSuffix (click)="applyFilter('')" title="Clear Search">
        <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
      </button>
      }
    </mat-form-field>
  </div>
  }
  <div class="tableFixHead">
    <!-- scenario event table -->
    <table>
      <!-- table header -->
      <thead matSort (matSortChange)="sortChanged($event)">
        <!-- main header row -->
        <tr>
          <!-- action button header -->
          <th class="background center-self">
            <div class="row-start-buttons">
              <span class="row-index center-self" [style.visibility]="allowDragAndDrop && canEditMsel() ? 'visible' : 'hidden'">&nbsp;</span>
              @if (!showSearch) {
              <button mat-icon-button (click)="setSearch(true)" class="header-button"
                title="Search Events">
                <mat-icon class="mdi-24px" fontIcon="mdi-filter-outline"></mat-icon>
              </button>
              }
              @if (showSearch) {
              <button mat-icon-button (click)="setSearch(false)" class="header-button"
                title="Clear Search">
                <mat-icon class="mdi-24px" fontIcon="mdi-filter-off-outline"></mat-icon>
              </button>
              }
              @if ((canEditMsel() || msel.hasRole(loggedInUserId, null).owner)) {
              <mat-checkbox [checked]="allSelected" (change)="setAllSelectedState($event.checked)"
                title="Select All/None" style="margin-top: 7px; margin-right: 7px;"
                [disabled]="displayedScenarioEvents.length === 0"></mat-checkbox>
              }
              @if (selectedEventIdList.length === 0 && (canEditMsel() || msel.hasRole(loggedInUserId, null).owner))
              {
              <button mat-icon-button class="header-button" title="Action List"
                [matMenuTriggerFor]="addEventList">
                <mat-icon class="mdi-24px" fontIcon="mdi-menu"></mat-icon>
              </button>
              }
              <mat-menu #addEventList="matMenu" [overlapTrigger]="false">
                @if ((canEditMsel() || msel.hasRole(loggedInUserId, null).owner) && selectedEventIdList.length ===
                0) {
                <button mat-menu-item (click)="addScenarioEvent()" title="Add an event"
                  [disabled]="allDataFields.length === 0">
                  Add New Event
                </button>
                }
                @if (catalogList.length > 0 && (canEditMsel() || msel.hasRole(loggedInUserId, null).owner)) {
                <button mat-menu-item [matMenuTriggerFor]="addFromCatalog" title="Add an inject from a catalog"
                  [disabled]="selectedEventIdList.length > 0">
                  Add Inject from Catalog
                </button>
                }
              </mat-menu>
              <mat-menu #addFromCatalog="matMenu" [overlapTrigger]="false">
                @for (catalog of catalogList; track catalog.id) {
                <button mat-menu-item (click)="selectFromCatalog(catalog)" title="{{ catalog.name }}">
                  {{ catalog.name }}
                </button>
                }
              </mat-menu>
              @if (selectedEventIdList.length > 0 && (canEditMsel() || msel.hasRole(loggedInUserId, null).owner)) {
              <button mat-icon-button class="header-button" title="Action List"
                [matMenuTriggerFor]="bulkEventList">
                <mat-icon class="mdi-24px" fontIcon="mdi-menu"></mat-icon>
              </button>
              }
              <mat-menu #bulkEventList="matMenu" [overlapTrigger]="false">
                @if ((canEditMsel() || msel.hasRole(loggedInUserId, null).owner)) {
                <button mat-menu-item (click)="copyToAnotherMsel()" title="Copy Selected Events">
                  Copy Selected Events
                </button>
                }
                @if ((canEditMsel() || msel.hasRole(loggedInUserId, null).owner)) {
                <button mat-menu-item [matMenuTriggerFor]="colorList" (click)="$event.stopPropagation()"
                  title="Highlight selected events">
                  Highlight selected events
                </button>
                }
                <mat-menu #colorList="matMenu" [overlapTrigger]="false">
                  <button mat-menu-item (click)="batchSelectNewColor('')" class="color-option-button">
                    <div class="color-option" [style]="getStyleFromColor('')"></div>
                  </button>
                  @for (color of scenarioEventBackgroundColors; track color) {
                  <button mat-menu-item (click)="batchSelectNewColor(color)" class="color-option-button">
                    <div class="color-option" [style]="getStyleFromColor(color)">&nbsp;</div>
                  </button>
                  }
                </mat-menu>
                <button mat-menu-item (click)="batchDeleteScenarioEvents()" title="Delete selecetd events">
                  Delete selected events
                </button>
              </mat-menu>
            </div>
          </th>
          <!-- Move header -->
          @if (msel.showMoveOnScenarioEventList) {
          <th mat-sort-header="moveNumber" class="background">
            <div>Move</div>
          </th>
          }
          <!-- Group header -->
          @if (msel.showGroupOnScenarioEventList) {
          <th mat-sort-header="groupOrder" class="background">
            <div>Group</div>
          </th>
          }
          <!-- Time header -->
          @if (msel.showTimeOnScenarioEventList) {
          <th mat-sort-header="deltaSeconds" class="background">
            <div>
              @if (!showRealTime) {
              <button mat-icon-button (click)="setRealTime(true)" style="outline: none;" title="Switch to Real Time"
                [disabled]="!msel || !msel.startTime || msel.startTime.valueOf() < 0">
                <mat-icon class="mdi-24px" fontIcon="mdi-timer-outline"></mat-icon>
              </button>
              }
              @if (showRealTime) {
              <button mat-icon-button (click)="setRealTime(false)" style="outline: none;" title="Switch to Delta Time">
                <mat-icon class="mdi-24px" fontIcon="mdi-clock-time-four-outline"></mat-icon>
              </button>
              }
              <span>Execution Time</span>
            </div>
          </th>
          }
          <!-- IntegrationTarget header -->
          @if (msel.showIntegrationTargetOnScenarioEventList) {
          <th mat-sort-header="integrationTarget" class="background">
            <div>Integration Target</div>
          </th>
          }
          <!-- Data Field headers -->
          @for (df of headerDataFields; track df.id) {
          @if (sortableDataTypes.includes(df.dataType)) {
          <th mat-sort-header="{{ df.name }}" class="background">
            <div [style]="getStyle(df)">{{ df.name }}</div>
          </th>
          } @else {
          <th class="background">
            <div [style]="getStyle(df)">{{ df.name }}</div>
          </th>
          }
          }
        </tr>
      </thead>
      <!-- table body -->
      <tbody cdkDropList [cdkDropListDisabled]="!allowDragAndDrop || !canEditMsel()"
        (cdkDropListDropped)="dropHandler($event)">
        @for (item of displayedScenarioEvents; track item.id; let rowIndex = $index) {
        @if (isMoveStartRow(rowIndex)) {
        <tr class="move-start-row">
          <td [attr.colspan]="999">{{ getMoveLabel(rowIndex) }}</td>
        </tr>
        }
        <tr [style]="getRowStyle(item)" cdkDrag [cdkDragData]="item" cdkDragLockAxis="y"
          [cdkDragPreviewClass]="userTheme" (cdkDragStarted)="dragStart($event)" (cdkDragEnded)="dragEnd($event)">
          <!-- row buttons -->
          <td class="center-self">
            <div class="row-start-buttons">
              <div cdkDragHandle class="center-self" [style.visibility]="allowDragAndDrop && canEditMsel() ? 'visible' : 'hidden'">
                <span><mat-icon class="mdi-24px" fontIcon="mdi-drag-vertical" /></span>
              </div>
              <span class="row-index center-self">{{ rowIndex + 1 }}</span>
              @if ((canEditMsel() || msel.hasRole(loggedInUserId, null).owner)) {
              <mat-checkbox [checked]="getSelectedState(item.id)" (change)="setSelectedState(item.id, $event.checked)"
                title="Select event {{ rowIndex  + 1 }}"></mat-checkbox>
              }
              <button mat-icon-button class="scenario-event-button" [matMenuTriggerFor]="actionList"
                (click)="$event.stopPropagation()" title="Event {{ rowIndex + 1 }} Action List"
                [disabled]="selectedEventIdList.length > 0">
                <mat-icon class="mdi-24px" fontIcon="mdi-menu"></mat-icon>
              </button>
              <mat-menu #actionList="matMenu" [overlapTrigger]="false">
                <button mat-menu-item (click)="openContent(item.id)" title="Open page in new tab">
                  View
                </button>
                <button mat-menu-item (click)="editScenarioEvent(item)" title="Edit event {{ rowIndex  + 1 }}">
                  Edit
                </button>
                @if ((canEditMsel() || msel.hasRole(loggedInUserId, item.id).owner)) {
                <button mat-menu-item [matMenuTriggerFor]="colorList" (click)="$event.stopPropagation()"
                  title="Highlight event {{ rowIndex  + 1 }}">
                  Highlight
                </button>
                }
                <mat-menu #colorList="matMenu" [overlapTrigger]="false">
                  <button mat-menu-item (click)="selectNewColor('', item)" class="color-option-button">
                    <div class="color-option" [style]="getStyleFromColor('')"></div>
                  </button>
                  @for (color of scenarioEventBackgroundColors; track color) {
                  <button mat-menu-item (click)="selectNewColor(color, item)" class="color-option-button">
                    <div class="color-option" [style]="getStyleFromColor(color)">&nbsp;</div>
                  </button>
                  }
                </mat-menu>
                @if ((canEditMsel() || msel.hasRole(loggedInUserId, item.id).owner) && !isStarterMsel) {
                <button mat-menu-item (click)="copyScenarioEvent(item)" title="Copy event {{ rowIndex + 1 }}">
                  Copy
                </button>
                }
                @if (canEditMsel() || msel.hasRole(loggedInUserId, item.id).owner) {
                <button mat-menu-item (click)="deleteScenarioEvent(item, rowIndex + 1)"
                  title="Delete event {{ rowIndex  + 1 }}">
                  Delete
                </button>
                }
              </mat-menu>
              @if (!item.isHidden && !isStarterMsel) {
              <button mat-icon-button class="scenario-event-button" (click)="setHiddenScenarioEvent(item, true)"
                title="Hide event {{ rowIndex + 1 }} on the Exercise View"
                [disabled]="selectedEventIdList.length > 0 || !canEditMsel()">
                <mat-icon class="mdi-24px" fontIcon="mdi-eye-outline"></mat-icon>
              </button>
              }
              @if (item.isHidden && !isStarterMsel) {
              <button mat-icon-button class="scenario-event-button" (click)="setHiddenScenarioEvent(item, false)"
                title="Unhide event {{ rowIndex + 1 }} on the Exercise View"
                [disabled]="selectedEventIdList.length > 0">
                <mat-icon class="mdi-24px" fontIcon="mdi-eye-off-outline"></mat-icon>
              </button>
              }
            </div>
          </td>
          <!-- Move value -->
          @if (msel.showMoveOnScenarioEventList) {
          <td>
            <div class="constrainer" style="text-align: center;">
              {{ moveAndGroupNumbers[item.id][0] }}
            </div>
          </td>
          }
          <!-- Group value -->
          @if (msel.showGroupOnScenarioEventList) {
          <td>
            <div class="constrainer" style="text-align: center;">
              {{ moveAndGroupNumbers[item.id][1] }}
            </div>
          </td>
          }
          <!-- Time value -->
          @if (msel.showTimeOnScenarioEventList) {
          <td>
            <div class="time-span center-self">
              <app-duration-view [startTime]="msel.startTime" [durationSeconds]="item.deltaSeconds"
                [showRealTime]="showRealTime"></app-duration-view>
            </div>
          </td>
          }
          <!-- Integration Target value -->
          @if (msel.showIntegrationTargetOnScenarioEventList) {
          <td>
            <mat-select multiple placeholder="Integration Target" (click)="$event.stopPropagation()"
              [value]="getIntegrationTargetArray(item)"
              (valueChange)="onIntegrationTargetChange(item, $event)"
              [disabled]="!((canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).editor) && !isStarterMsel)">
              @for (opt of integrationTargetOptions; track opt) {
              <mat-option [value]="opt">{{ opt }}</mat-option>
              }
            </mat-select>
          </td>
          }
          @if (item.scenarioEventType === eventType.Inject) {
          @for (df of rowDataFields(item); track df.id) {
          <td>
            <app-data-value [value]="getDataValue(item, df.name).value"
              (valueChange)="saveDataValue(item, df.name, $event)" [dataField]="df"
              [canEdit]="(canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).editor) && !isStarterMsel"
              [canApprove]="(canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).approver) && !isStarterMsel"
              [isOwner]="(canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).owner) && !isStarterMsel"
              [showValueOnly]="true" [organizationOptions]="getSortedOrganizationOptions()" [teamOptions]="teamList"
              [unitOptions]="unitList" [cardOptions]="cardList" [moveOptions]="moveList"
              [gallerySourceTypeOptions]="msel.gallerySourceTypes"
              [galleryItemStatusOptions]="msel.galleryItemStatuses" [userOptions]="userList">
            </app-data-value>
          </td>
          }
          }
          @if (item.scenarioEventType !== eventType.Inject) {
          <td colspan="99">
            <span style="margin-right: 30px;">{{ item.scenarioEventType }}:</span>
            @for (df2 of rowDataFields(item); track df2.id) {
            <span style="margin-right: 30px;">
              <app-data-value [value]="getDataValue(item, df2.name).value"
                (valueChange)="saveDataValue(item, df2.name, $event)" [dataField]="df2"
                [canEdit]="(canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).editor) && !isStarterMsel"
                [canApprove]="(canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).approver) && !isStarterMsel"
                [isOwner]="(canEditMsel() || msel.hasRole(this.loggedInUserId, item.id).owner) && !isStarterMsel"
                [showValueOnly]="true" [organizationOptions]="getSortedOrganizationOptions()" [teamOptions]="teamList"
                [unitOptions]="unitList" [cardOptions]="cardList" [moveOptions]="moveList"
                [gallerySourceTypeOptions]="msel.gallerySourceTypes"
                [galleryItemStatusOptions]="msel.galleryItemStatuses" [userOptions]="userList">
              </app-data-value>
            </span>
            }
            @if (item.information) {
            <span style="margin-right: 30px;">{{ item.information }}</span>
            }
          </td>
          }
        </tr>
        }
      </tbody>
    </table>
  </div>
  }



</div>
